import { BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { QESButton } from "@app/shared/qes-standards";
import { JSX, useContext, useEffect, useState } from "react";
import { UiStastiticsCollapseContextProvider, UiStatisticsCollapseContext } from "../contexts/UiStatisticsCollapseContext";
import { UiStatisticsCollapse } from "../shared/UiStastiticsCollapse";
import { PredefinedJobBarChartWrapper } from "./PredefinedJobBarChartWrapper";
import { JobStastiticsContext, JobStatisticsContextProvider } from "./JobStatisticsContext";
import { NewUserDefinedChartPanelContent } from "./NewUserDefinedChartPanelContent";
import { UserDefinedJobLineChartWrapper } from "./UserDefinedJobLineChartWrapper";
import { FlyoutLink } from "@app/shared/Flyouts";

const JobStatistics: React.FC<React.PropsWithChildren<unknown>> = () => {
  const jobStatisticsContext = useContext(JobStastiticsContext);
  const collapseContext = useContext(UiStatisticsCollapseContext);
  const [chartComponents, setChartComponents] = useState<JSX.Element[]>([]);

  useEffect(() => {
    Promise.all([buildPredefinedElements(), buildUserDefinedElements()]).then((results) => {
      const newComponents = results.reduce((prev, current) => { prev.push(...current); return prev; }, [])
      setChartComponents(newComponents);
    });
  }, [jobStatisticsContext.predefinedCharts, jobStatisticsContext.userDefinedCharts]);

  /* No dependencies on this useEffect because the chart that needs highlighting might not exist when the updatedChartId change happens,
   * so we first need to make sure its category is expanded, but we don't know when that's done and rendered. So this will get hit again the next time through.
   */
  useEffect(() => {
    if (jobStatisticsContext.updatedChartId) {
      if (!document.querySelector(`[data-id="${jobStatisticsContext.updatedChartId}"]`)) {
        collapseContext.expandCategory("User-Defined Charts");
      } else {
        jobStatisticsContext.highlightChart(jobStatisticsContext.updatedChartId);
      }
    }
  });

  const buildPredefinedElements = async () => {
    const newCharts: JSX.Element[] = [];
    jobStatisticsContext.predefinedCharts.forEach((values, key) => {
      const graphs = values.map((value, index) => {
        return <div className="job-stats__chart-container" key={index}> <PredefinedJobBarChartWrapper chartModel={value} /></div>;
      });
      newCharts.push(<UiStatisticsCollapse title={key} key={key} graphs={graphs} />);
    });
    return newCharts;
  }

  const buildUserDefinedElements = async () => {
    const newCharts: JSX.Element[] = [];
    jobStatisticsContext.userDefinedCharts.forEach((chartData, chartId) => {
      newCharts.push(<div className="job-stats__chart-container" key={chartId} data-id={chartId}> <UserDefinedJobLineChartWrapper chartModel={chartData} /></div>);
    });
    return [<UiStatisticsCollapse title="User-Defined Charts" key="user-defined" graphs={newCharts} />];
  }

  return (
    <>
      <Page documentTitle="Job Statistics">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.Performance}
          items={[
            {
              iconClass: "fa-server",
              text: "Job Statistics",
            },
          ]}
        />
        <PageInner>
          <PageHeader title="Job Statistics">
            <PageHeaderRight>
              <FlyoutLink
                content={{
                  body: <NewUserDefinedChartPanelContent />,
                  title: "Define a New Chart",
                }}
                size="40"
                classNames="qes-button-primary"
                testId="job-stats__new-chart-sidepanel"
              >
                Define New Chart
              </FlyoutLink>
            </PageHeaderRight>
          </PageHeader>
          <QESButton qesType="secondary" qesTestId="job-stats__refresh-all" onClick={jobStatisticsContext.refreshAllPredefinedCharts}>Refresh Predefined Charts</QESButton>
          <PageSection>
            {chartComponents}
          </PageSection>
        </PageInner>
      </Page>
    </>
  );
}

const JobStatisticsWrapper: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <JobStatisticsContextProvider>
      <UiStastiticsCollapseContextProvider>
        <JobStatistics />
      </UiStastiticsCollapseContextProvider>
    </JobStatisticsContextProvider>
  )
}

export { JobStatisticsWrapper as JobStatistics };